import { useState, useRef, useEffect } from "react";
import {
  Paper,
  Group,
  Stack,
  Textarea,
  Loader,
  Text,
  Container,
  Grid,
  Button,
  rem,
  Alert,
  ScrollAreaAutosize,
  Center,
  Badge,
  Box,
} from "@mantine/core";
import { useIsomorphicEffect } from "@mantine/hooks";
import { IconSend, IconUsersGroup, IconArrowDown } from "@tabler/icons-react";
import { useStore } from "../store";
import { getPanelTurns, sendPanelMessage } from "./panelApi";
import PanelSessionList from "./PanelSessionList";
import PanelSessionConfig from "./PanelSessionConfig";
import PanelTurnView from "./PanelTurnView";
import { TextDisplayModeControl } from "../_Common/RenderedText";

function QuestionBubble({ text }) {
  return (
    <Group justify="flex-end" mb="xs">
      <Paper
        radius="md"
        p="xs"
        maw="80%"
        bg="var(--mantine-color-myColor-light, var(--mantine-color-blue-light))"
      >
        <Text style={{ whiteSpace: "pre-wrap" }}>{text}</Text>
      </Paper>
    </Group>
  );
}

export default function PanelChatPage() {
  const session = useStore((state) => state.selectedPanelSession);

  const [turns, setTurns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState("");
  const [thinking, setThinking] = useState(false);
  const [pending, setPending] = useState(null);
  // One rendering mode for the whole conversation — a per-turn switch would mean
  // deciding it again for every answer.
  const [displayMode, setDisplayMode] = useState("markdown");
  const viewport = useRef(null);

  const scrollToBottom = () =>
    viewport.current?.scrollTo({
      top: viewport.current.scrollHeight,
      behavior: "smooth",
    });

  useEffect(() => {
    if (!session?.id) {
      setTurns([]);
      return;
    }
    setLoading(true);
    getPanelTurns(session.id)
      .then((data) => setTurns(data ?? []))
      .finally(() => setLoading(false));
  }, [session?.id]);

  useIsomorphicEffect(() => {
    if (viewport.current) scrollToBottom();
  }, [turns, thinking]);

  const send = () => {
    const q = question.trim();
    if (!session?.id || !q) return;
    setThinking(true);
    setPending(q);
    setQuestion("");
    sendPanelMessage(session.id, q)
      .then((turn) => {
        setTurns((prev) => [...prev, turn]);
      })
      .catch((err) => {
        setTurns((prev) => [
          ...prev,
          {
            id: `err-${Date.now()}`,
            userQuestion: q,
            synthesizedAnswer: `Something went wrong: ${err.message}`,
            agreements: [],
            dissents: [],
            contributions: [],
          },
        ]);
      })
      .finally(() => {
        setThinking(false);
        setPending(null);
      });
  };

  return (
    <Container px={0} fluid>
      <Alert mb="md" icon={<IconUsersGroup />}>
        Convene a panel of models with different personas on a hard question. Each
        seat answers independently, then a chair synthesizes one answer and
        surfaces where they disagreed. Every turn runs several models, so panel
        conversations are slower and more expensive than a single chat.
      </Alert>

      <Grid>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <ScrollAreaAutosize mah="calc(90dvh - 180px)">
            <PanelSessionList />
          </ScrollAreaAutosize>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 8 }}>
          {!session ? (
            <PanelSessionConfig />
          ) : (
            <Stack gap="xs">
              <Group justify="space-between">
                <Group gap="xs">
                  <Text fw={600}>{session.title}</Text>
                  <Badge variant="light" size="sm">
                    {session.panel?.name}
                  </Badge>
                </Group>
                <TextDisplayModeControl
                  value={displayMode}
                  onChange={setDisplayMode}
                  label="How the panel's answers are rendered"
                />
              </Group>

              <ScrollAreaAutosize
                h="calc(100vh - var(--app-shell-header-height, 0px) - 300px)"
                viewportRef={viewport}
              >
                {loading && (
                  <Center mt="xl">
                    <Loader />
                  </Center>
                )}

                {!loading && turns.length === 0 && !thinking && (
                  <Center mih={140} c="dimmed">
                    <Stack align="center" gap={6}>
                      <IconUsersGroup size={30} stroke={1.5} />
                      <Text size="sm">No questions yet.</Text>
                      <Group gap={6}>
                        <Text size="sm">Type your first question below and hit Send</Text>
                        <IconArrowDown size={16} stroke={1.5} />
                      </Group>
                    </Stack>
                  </Center>
                )}

                {turns.map((turn) => (
                  <Box key={turn.id}>
                    <QuestionBubble text={turn.userQuestion} />
                    <PanelTurnView turn={turn} displayMode={displayMode} />
                  </Box>
                ))}

                {thinking && (
                  <Box>
                    <QuestionBubble text={pending} />
                    <Group gap="xs" mb="md" c="dimmed">
                      <Loader size="sm" />
                      <Text size="sm">The panel is deliberating…</Text>
                    </Group>
                  </Box>
                )}
              </ScrollAreaAutosize>

              <Paper withBorder radius="md" p="sm" mt="sm">
                <Stack gap="xs">
                  <Textarea
                    autosize
                    minRows={1}
                    maxRows={6}
                    radius="md"
                    placeholder="Ask the panel a hard question…"
                    value={question}
                    onChange={(e) => setQuestion(e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        send();
                      }
                    }}
                  />
                  <Group justify="space-between">
                    <Text size="xs" c="dimmed">
                      ⌘/Ctrl + Enter to send
                    </Text>
                    <Button
                      onClick={send}
                      loading={thinking}
                      disabled={thinking || question.trim() === ""}
                      leftSection={
                        <IconSend
                          style={{ width: rem(18), height: rem(18) }}
                          stroke={1.8}
                        />
                      }
                    >
                      Send
                    </Button>
                  </Group>
                </Stack>
              </Paper>
            </Stack>
          )}
        </Grid.Col>
      </Grid>
    </Container>
  );
}
